import { useReactiveVar } from '@apollo/client';
import {
  AppBar,
  Button,
  darken,
  Divider,
  Skeleton,
  Toolbar,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from '@tanstack/react-router';
import PropTypes from 'prop-types';
import { useLayoutEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';

// import icons
import { ArrowLeft as LeftIcon } from '@mui/icons-material';

// relative imports
import { switchingAccountsVar } from '../../../data/apollo/cache/reactiveVars';
import {
  getAppConfig,
  isFeatureEnabled,
} from '../../../data/app-config/configs/configUtilities';
import UpdateDataSourcesButton from '../../account-management/account-updates/UpdateAccountDataButton';
import TrialUpgradeButton from '../../billing/subscriptions/TrialUpgradeButton';
import PendingCodeInviteButton from '../../client-management/PendingCodeInviteButton';
import { useAccountConfig } from '../../providers/AccountConfigProvider';
import { useAccount } from '../../providers/AccountProvider';
import { useLedgibleAuth } from '../../providers/LedgibleAuthProvider';
import PaymentSourceExpiryAlert from '../custom-components/PaymentSourceExpiryAlert';
import useAlternativeDisplay from '../custom-hooks/useAlternativeDisplay';
import usePaymentSourceExpiry from '../custom-hooks/usePaymentSourceExpiry';
import HelpMenu from '../customer-support/HelpMenu';
import { getAppLogoSrc, getContextualAppPath } from '../utilities/appUtilities';
import AccountWindow from './account-window/AccountWindow';
import AdminClientViewBar from './AdminClientViewBar';
import ProfileNavbox from './ProfileNavbox';
import SecondaryToolbar from './SecondaryToolbar';

const useStyles = makeStyles()((theme, { isMobile }) => ({
  appBar: {
    backgroundColor:
      theme.whiteLabel.Header.color || theme.palette.secondary.main,
  },
  toolbarMain: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    justifyContent: 'space-between',
    alignItems: 'stretch',
    minHeight: '56px !important',
  },
  toolbarMainGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    color: theme.whiteLabel.Navigation.color || theme.palette.grey[100],
    marginLeft: theme.spacing(2),
    '&:hover': {
      color: theme.whiteLabel.Navigation.color
        ? darken(theme.whiteLabel.Navigation.color, 1)
        : theme.palette.grey[300],
      backgroundColor: 'transparent',
    },
  },
  updatingText: {
    color: '#fff',
  },
  notificationsButton: {
    color: theme.whiteLabel.Navigation.color || theme.palette.common.white,
    padding: 5,
    marginRight: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.2)',
    },
  },
  notificationsIcon: {
    margin: -2,
  },
  titleBox: {
    borderLeft: `${isMobile ? 0 : 1}px solid ${theme.palette.grey[200]}`,
  },
  productName: {
    width: '100%',
    fontSize: theme.typography.pxToRem(18),
    lineHeight: '30px',
    fontWeight: 800,
    color: theme.whiteLabel.ProductName.color || theme.palette.common.white,
    whiteSpace: 'nowrap',
  },
  productNameDivider: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    width: '2px',
    height: '30px',
    backgroundColor: theme.palette.grey[700],
  },
}));

const Header = ({
  hideAccountMenu,
  hideSecondaryMenu,
  hideUpdateButton,
  setHeaderHeight,
}) => {
  const { isMobile } = useAlternativeDisplay();
  const appConfig = getAppConfig();
  const navigate = useNavigate();
  const { classes } = useStyles({ isMobile });
  const { state: locationState } = useLocation();
  const { user } = useLedgibleAuth();
  const accountData = useAccount();
  const switchingAccounts = useReactiveVar(switchingAccountsVar);
  const { clientPermissions: permissions = {}, currentAccount = {} } =
    accountData || {};
  const accountConfigData = useAccountConfig();
  const { accountConfig } = accountConfigData || {};
  const { show: showExpiryBar } = usePaymentSourceExpiry();
  const headerRef = useRef();
  const shouldDisplayUpdateButton =
    isFeatureEnabled({
      moduleName: 'sourceManagement',
      featureName: 'triggerSourceUpdates',
    }) ||
    isFeatureEnabled({
      moduleName: 'tax',
      featureName: 'triggerTaxUpdate',
    });

  useLayoutEffect(() => {
    setHeaderHeight(headerRef?.current?.clientHeight);
  });

  return (
    <AppBar className={classes.appBar} position="fixed" ref={headerRef}>
      {permissions.isAdminViewingClient && !switchingAccounts && (
        <AdminClientViewBar />
      )}
      {showExpiryBar && <PaymentSourceExpiryAlert />}
      <Toolbar className={classes.toolbarMain}>
        {switchingAccounts && (
          <div className={classes.toolbarMainGroup}>
            <Skeleton
              variant="rounded"
              height="30px"
              width="180px"
              animation="wave"
              sx={{
                bgcolor: 'grey.100',
                opacity: 0.25,
              }}
            />
          </div>
        )}
        {!switchingAccounts && (
          <>
            <div className={classes.toolbarMainGroup}>
              <img
                src={getAppLogoSrc()}
                alt="primary logo"
                data-cy="primary-logo"
                height="30px"
              />
              {appConfig.productName && (
                <>
                  <Divider
                    className={classes.productNameDivider}
                    orientation="vertical"
                  />
                  <Typography className={classes.productName}>
                    {appConfig.productName}
                  </Typography>
                </>
              )}
              {!hideAccountMenu && <AccountWindow />}
              {hideAccountMenu && (
                <Button
                  onClick={() => {
                    if (locationState?.redirect) {
                      navigate({ to: locationState.redirect });
                    } else {
                      navigate({ to: getContextualAppPath('/') });
                    }
                  }}
                  className={classes.backButton}
                  variant="text"
                  id="TopNav-BackToDashboardWithRedirectBtn"
                >
                  <LeftIcon />
                  {locationState?.redirectLabel || 'Back'}
                </Button>
              )}
              {!hideAccountMenu &&
                currentAccount.id &&
                !hideUpdateButton &&
                shouldDisplayUpdateButton && <UpdateDataSourcesButton />}
            </div>
            <div className={classes.toolbarMainGroup}>
              {!hideAccountMenu &&
                currentAccount.inTrial &&
                !currentAccount.trialEnded && (
                  <TrialUpgradeButton
                    variant="outlined"
                    size="small"
                    marginRight={8}
                  />
                )}
              {isFeatureEnabled({
                moduleName: 'client',
                featureName: 'connectToAdmin',
              }) &&
                !permissions.isAdminViewingClient && (
                  <PendingCodeInviteButton />
                )}
              <HelpMenu />
              {user && <ProfileNavbox />}
            </div>
          </>
        )}
      </Toolbar>
      {!switchingAccounts &&
        accountConfig?.id === currentAccount.applicationType?.id &&
        accountConfig?.enabledPageIds &&
        !hideSecondaryMenu && <SecondaryToolbar />}
    </AppBar>
  );
};

export default Header;

Header.propTypes = {
  setHeaderHeight: PropTypes.func.isRequired,
  hideAccountMenu: PropTypes.bool,
  hideSecondaryMenu: PropTypes.bool,
  hideUpdateButton: PropTypes.bool,
};

Header.defaultProps = {
  hideAccountMenu: false,
  hideSecondaryMenu: false,
  hideUpdateButton: false,
};
